/**
 * Locale persistence for Hanko Menu.
 *
 * The widget's language is normally driven by the `lang` attribute set by the
 * host application. Storage acts as a fallback for hosts that don't set the
 * attribute, and keeps the locale across reloads.
 *
 * The `language` key is shared with the host application, so values are written
 * in the host's format (`'en' | 'ja-JP'`). Writing the widget's internal short
 * form (`'ja'`) would fail MyVaisala's equality check and silently discard the
 * user's preference.
 *
 * Storage access is wrapped in try/catch — `localStorage` throws in private
 * browsing modes and when disabled by policy, and a language preference is
 * never important enough to break rendering over.
 */

import type { SupportedLocale } from '@/types';
import { mapLocaleToLanguageTag, resolveLocale } from './locale';

/** Language key shared with the host application (MyVaisala's `LANGUAGE_STORAGE_KEY`). */
const LANGUAGE_STORAGE_KEY = 'language';

/** Persists the locale in the host's value format (`'en' | 'ja-JP'`). */
export function persistLocale(locale: SupportedLocale): void {
  try {
    globalThis.localStorage?.setItem(LANGUAGE_STORAGE_KEY, mapLocaleToLanguageTag(locale));
  } catch {
    // Storage unavailable — the locale simply won't persist across reloads.
  }
}

/**
 * Resolves the locale for first render: the host-provided `lang` attribute wins
 * as an explicit instruction, otherwise the stored `language` preference, then
 * `'en'`.
 *
 * Reading is tolerant of the host's format — `resolveLocale` extracts the
 * primary tag, so `'ja-JP'` yields `'ja'`.
 *
 * @param initialLang - Raw value of the host-provided `lang` attribute
 */
export function resolveInitialLocale(initialLang?: string): SupportedLocale {
  if (initialLang) {
    return resolveLocale(initialLang);
  }

  try {
    const stored = globalThis.localStorage?.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      return resolveLocale(stored);
    }
  } catch {
    // Storage unavailable — fall through to the default.
  }

  return 'en';
}
